"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.outListQuerySchema = exports.OUT_PAGE_SIZE = exports.OUT_OUTCOME_STAGES = exports.OUT_OUTCOMES = exports.SHORTLIST_TEST_OFF = exports.NO_RANKING = exports.NO_IMPORTS = exports.OUT_STAGES = exports.SELECTED_STAGES = exports.STAGE_BAR_STAGES = exports.STAGE_BAR_KEYS = void 0;
exports.isStageBarKey = isStageBarKey;
exports.stageBarJobOf = stageBarJobOf;
exports.stageBarKeyOf = stageBarKeyOf;
exports.stageBarStatesOf = stageBarStatesOf;
exports.countsToStageBar = countsToStageBar;
exports.stageBarLanding = stageBarLanding;
const zod_1 = require("zod");
const phone_screen_1 = require("./phone-screen");
// §L33 — the stage bar: a view over the existing application stages (D21), never a
// second pipeline. Review, Shortlist and Contact are steps inside `screening`, told apart
// by the three status fields (ADR 0037). QoL §15 and JOB-12 describe the screen.
exports.STAGE_BAR_KEYS = [
    'job',
    'import',
    'ranking',
    'review',
    'shortlist',
    'phone_screen',
    'contact',
    'assessment',
    'interview_1',
    'interview_2',
    'interview_3',
    'selected',
];
exports.STAGE_BAR_STAGES = [
    { key: 'job', label: 'Job', isBuilt: true, shownWhen: 'always' },
    { key: 'import', label: 'Import', isBuilt: true, shownWhen: 'always' },
    { key: 'ranking', label: 'Automated ranking', isBuilt: true, shownWhen: 'always' },
    { key: 'review', label: 'Review', isBuilt: true, shownWhen: 'always' },
    { key: 'shortlist', label: 'Shortlist', isBuilt: true, shownWhen: 'always' },
    // ADR 0079 (workflow review D2): the optional phone Screening step, only on a job that switched it on.
    { key: 'phone_screen', label: phone_screen_1.PHONE_SCREEN_LABEL, isBuilt: true, shownWhen: 'phone_screen' },
    // R6 (DECISIONS-LOG "Start R6" Q2): a plain list of the Yes and HR-went-ahead people until R7's tracker.
    { key: 'contact', label: 'Contact', isBuilt: true, shownWhen: 'always' },
    { key: 'assessment', label: 'Assessment', isBuilt: true, shownWhen: 'assessment' },
    { key: 'interview_1', label: 'Interview 1', isBuilt: true, shownWhen: 'round' },
    { key: 'interview_2', label: 'Interview 2', isBuilt: true, shownWhen: 'round' },
    { key: 'interview_3', label: 'Interview 3', isBuilt: true, shownWhen: 'round' },
    { key: 'selected', label: 'Selected', isBuilt: true, shownWhen: 'always' },
];
function isStageBarKey(value) {
    return exports.STAGE_BAR_KEYS.includes(value);
}
/** §L33: Selected is `offer` and later. */
exports.SELECTED_STAGES = ['offer', 'hired_pending_join', 'joined'];
/** §L33 "Out": not a stage, a link at the bar's right end. */
exports.OUT_STAGES = ['rejected', 'withdrawn', 'closed_job'];
exports.NO_IMPORTS = { pending: 0, imported: 0 };
exports.NO_RANKING = { pending: 0, ranked: 0 };
const INTERVIEW_BAR_STAGES = ['interview_1', 'interview_2', 'interview_3'];
/** What the bar needs to know about a job: the stages its pipeline has switched on, and its setup. */
function stageBarJobOf(activeStages, setupMissing, status, usesPhoneScreen = false) {
    return {
        usesAssessment: activeStages.has('assessment'),
        activeInterviewStages: INTERVIEW_BAR_STAGES.filter((stage) => activeStages.has(stage)),
        setupMissing,
        isDraft: status === 'draft',
        usesPhoneScreen,
    };
}
/** Which bar stage a live application sits in, or null when it's in none (hold, auto-rejected, Out). */
/** ADR 0047: the switched-off refusal for "Send test" from ⑤ Shortlist. */
exports.SHORTLIST_TEST_OFF = 'Sending the test straight from Shortlist is switched off. An admin can switch it on in System settings, or move them to Contact first.';
function stageBarKeyOf(row) {
    if (row.stage === 'screening') {
        const step = screeningStepOf(row.reviewStatus, row.managerResponse);
        // ADR 0079: past the Shortlist but still being phoned sits in Screening, not Contact.
        return step === 'contact' && row.phoneScreen === 'in_screening' ? 'phone_screen' : step;
    }
    if (row.stage === 'assessment' || row.stage === 'interview_1' || row.stage === 'interview_2' || row.stage === 'interview_3')
        return row.stage;
    if (exports.SELECTED_STAGES.includes(row.stage))
        return 'selected';
    return null;
}
/**
 * §L33 rows ④–⑥, all inside `screening`. A null review status counts as `to_review`:
 * §L25 starts every screening application there.
 */
function screeningStepOf(review, manager) {
    if (review === null || review === 'to_review' || review === 'maybe')
        return 'review';
    if (manager === 'yes' || manager === 'hr_proceeded')
        return 'contact';
    if (manager === null || manager === 'not_sent' || manager === 'awaiting')
        return 'shortlist';
    // A manager's No waits for HR to reject it (§L31). It belongs to no stage.
    return null;
}
/**
 * Built, and either used by the job or still holding someone: a stage switched off after
 * people reached it stays until they move on, as the pipeline's columns do (S16).
 */
function isDrawn(definition, job, count) {
    if (!definition.isBuilt)
        return false;
    if (count > 0)
        return true;
    if (definition.shownWhen === 'assessment')
        return job.usesAssessment;
    if (definition.shownWhen === 'round')
        return job.activeInterviewStages.includes(definition.key);
    if (definition.shownWhen === 'phone_screen')
        return job.usesPhoneScreen === true;
    return true;
}
/**
 * §L33 state. ① Job is current while the job is a draft, and done once it opens. After
 * that, the leftmost occupied stage is current; an empty stage that someone has passed is
 * done; everything else is upcoming.
 */
function stageBarStatesOf(occupancy) {
    const currentIndex = occupancy.findIndex((stage) => stage.occupied);
    return occupancy.map((stage, index) => {
        if (index === currentIndex)
            return 'current';
        if (stage.key === 'job')
            return stage.occupied ? 'upcoming' : 'done';
        const isPassed = !stage.occupied && occupancy.slice(index + 1).some((later) => later.occupied);
        return isPassed ? 'done' : 'upcoming';
    });
}
function countsToStageBar(rows, job, imports = exports.NO_IMPORTS, ranking = exports.NO_RANKING) {
    const counts = new Map([
        ['import', imports.pending],
        ['ranking', ranking.pending],
    ]);
    const out = { rejected: 0, withdrawn: 0, closed: 0 };
    for (const row of rows) {
        if (row.stage === 'rejected')
            out.rejected += row.count;
        if (row.stage === 'withdrawn')
            out.withdrawn += row.count;
        if (row.stage === 'closed_job')
            out.closed += row.count;
        const key = stageBarKeyOf(row);
        if (key)
            counts.set(key, (counts.get(key) ?? 0) + row.count);
    }
    const drawn = exports.STAGE_BAR_STAGES.filter((definition) => isDrawn(definition, job, counts.get(definition.key) ?? 0));
    const states = stageBarStatesOf(drawn.map((definition) => ({
        key: definition.key,
        occupied: definition.key === 'job' ? job.isDraft : (counts.get(definition.key) ?? 0) > 0,
    })));
    const stages = drawn.map((definition, index) => ({
        key: definition.key,
        label: definition.label,
        number: definition.key === 'selected' ? null : index + 1,
        count: definition.key === 'job' ? null : (counts.get(definition.key) ?? 0),
        detail: detailOf(definition.key, imports, ranking),
        state: states[index] ?? 'upcoming',
    }));
    return { stages, setupMissing: job.setupMissing, out: { ...out, total: out.rejected + out.withdrawn + out.closed } };
}
/** The small text under ② and ③ (§L33): "980 imported", "950 ranked". */
function detailOf(key, imports, ranking) {
    if (key === 'import' && imports.imported > 0)
        return `${imports.imported.toLocaleString('en-US')} imported`;
    if (key === 'ranking' && ranking.ranked > 0)
        return `${ranking.ranked.toLocaleString('en-US')} ranked`;
    return null;
}
/** Where a job opens (DECISIONS-LOG 2026-10-12 Q2): its current stage, else ① Job. */
function stageBarLanding(bar) {
    return bar.stages.find((stage) => stage.state === 'current')?.key ?? 'job';
}
// --- "Out: N" (§L33) ---------------------------------------------------------------------
exports.OUT_OUTCOMES = ['rejected', 'withdrawn', 'closed'];
exports.OUT_OUTCOME_STAGES = {
    rejected: 'rejected',
    withdrawn: 'withdrawn',
    closed: 'closed_job',
};
exports.OUT_PAGE_SIZE = 50;
exports.outListQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().positive().default(1),
    outcome: zod_1.z.enum(exports.OUT_OUTCOMES).optional(),
});
//# sourceMappingURL=stage-bar.js.map